import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { classifyCaseTimelineKind } from '../../domain/safetyCases/caseTimelineKinds';
import { presentCaseHistory, presentCaseHistoryForDisplay, type CaseHistoryEventView } from '../../domain/safetyCases/caseHistoryPresenter';

type AuditRow = { at: string; label: string; detail: string };

type CaseHistoryPanelProps = {
  events: unknown;
  formatWhen: (value?: string | null, fallback?: string) => string;
  emptyCopy?: string;
  testId?: string;
  density?: 'summary' | 'full';
};

type CaseHistorySectionProps = {
  events: unknown;
  auditRows: AuditRow[];
  formatWhen: (value?: string | null, fallback?: string) => string;
  heading: string;
  emptyCopy: string;
  testId?: string;
  eventTestId?: string;
  density?: 'summary' | 'full';
};

function HistoryRow({
  row,
  formatWhen,
}: {
  row: CaseHistoryEventView;
  formatWhen: (value?: string | null, fallback?: string) => string;
}) {
  return (
    <Box sx={{ mb: 1.25 }} data-testid="safety-case-timeline-event">
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.06 }}>
        {row.kind || classifyCaseTimelineKind(row.title)}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }} data-testid={`case-history-title-${row.eventType}`}>
        {row.title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {formatWhen(row.occurredAt, 'Not recorded')}
      </Typography>
      {row.summary ? (
        <Typography variant="body2" color="text.secondary">{row.summary}</Typography>
      ) : null}
      {row.detailLines.map((line) => (
        <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
      ))}
      {row.href && row.linkLabel ? (
        <Typography
          component="a"
          href={row.href}
          variant="body2"
          sx={{ display: 'inline-block', mt: 0.25, fontWeight: 700, textDecoration: 'none' }}
        >
          {row.linkLabel}
        </Typography>
      ) : null}
    </Box>
  );
}

export const CaseHistoryPanel: React.FC<CaseHistoryPanelProps> = ({
  events,
  formatWhen,
  emptyCopy = 'Case lifecycle events will appear here as persisted history is recorded.',
  testId = 'safety-case-persisted-timeline',
  density = 'full',
}) => {
  const presented = presentCaseHistoryForDisplay(events, density);
  const rows = presented.rows;
  return (
    <Box data-testid={testId} data-history-density={density} data-collapsed-runs={presented.collapsedRunCount}>
      {rows.length ? rows.map((row) => (
        <HistoryRow key={row.eventId} row={row} formatWhen={formatWhen} />
      )) : (
        <Typography variant="body2" color="text.secondary">{emptyCopy}</Typography>
      )}
      {presented.collapsedRunCount > 0 ? (
        <Typography variant="caption" color="text.secondary" data-testid="case-history-collapsed-runs">
          {presented.collapsedRunCount} additional scheduled run{presented.collapsedRunCount === 1 ? '' : 's'} hidden.
        </Typography>
      ) : null}
    </Box>
  );
};

export const CaseHistorySection: React.FC<CaseHistorySectionProps> = ({
  events,
  auditRows,
  formatWhen,
  heading,
  emptyCopy,
  testId,
  eventTestId,
  density = 'full',
}) => {
  const goldenPath = presentCaseHistory(events);
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid={testId} data-history-density={density}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{heading}</Typography>
        {testId === 'safety-case-persisted-timeline' ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            {density === 'summary'
              ? 'Recent Case events. Full chronology is in More → Audit history.'
              : 'Only events persisted on this Safety Case are listed.'}
          </Typography>
        ) : null}
        {goldenPath.length ? (
          <CaseHistoryPanel events={events} formatWhen={formatWhen} testId={`${testId || 'case-history'}-events`} density={density} />
        ) : auditRows.length ? auditRows.map((row) => (
          <Box key={`${row.label}-${row.at}`} sx={{ mb: 1.25 }} data-testid={eventTestId}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.06 }}>
              {classifyCaseTimelineKind(row.label)}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatWhen(row.at, 'Not recorded')} · {row.detail}
            </Typography>
          </Box>
        )) : (
          <Typography variant="body2" color="text.secondary">{emptyCopy}</Typography>
        )}
      </CardContent>
    </Card>
  );
};
